import React, { useCallback, useEffect, useRef, useState } from "react";
import MsgDataService from "../../../services/MsgService";
import {
  hasCompleteApprovalDateRange,
  isApprovalDateRangeValid,
} from "../../../utils/approvalDateFilters";

export const EMPTY_APPROVAL_FILTERS = {
  role: "",
  userName: "",
  userId: "",
  district: "",
  districtId: "",
  college: "",
  collegeId: "",
  adminAction: "",
  status: "",
  fromDate: "",
  toDate: "",
};

const TYPEAHEAD_DEBOUNCE_MS = 400;
const MIN_TYPEAHEAD_LENGTH = 2;

const getListItems = (data) => {
  const payload = data?.data ?? data ?? {};
  if (Array.isArray(payload)) return payload;
  return payload.object || payload.objects || payload.results || [];
};

const normalizeOption = (item) => {
  if (typeof item === "string") {
    return { id: item, label: item };
  }
  const label =
    item?.name || item?.college_name || item?.district_name || item?.label || "";
  const id = item?.district_id ?? item?.college_id ?? item?.id;
  return { id: id != null ? String(id) : "", label };
};

const normalizeRequester = (item) => {
  if (typeof item === "string") {
    return { id: item, name: item, email: "", displayName: item };
  }
  const displayName =
    item?.display_name || item?.name || item?.user_name || "";
  let name = displayName;
  let email = item?.email || "";
  const parenMatch = displayName.match(/^(.+?)\s*\(([^)]+)\)\s*$/);
  if (parenMatch) {
    name = parenMatch[1].trim();
    if (!email) email = parenMatch[2].trim();
  }
  const id = item?.user_id ?? item?.id;
  return {
    id: id != null ? String(id) : "",
    name,
    email,
    displayName: displayName || name,
  };
};

const RequesterTypeaheadField = ({
  value,
  placeholder,
  options,
  loading,
  showList,
  onShowList,
  onChange,
  onSelect,
}) => {
  const normalizedOptions = options
    .map(normalizeRequester)
    .filter((opt) => opt.displayName || opt.name || opt.email);
  const hasOptions = normalizedOptions.length > 0;
  const showDropdown =
    showList && (loading || hasOptions || value?.trim().length >= MIN_TYPEAHEAD_LENGTH);

  return (
    <div className="messages-approval-filter-field messages-approval-filter-field--typeahead">
      <label>User Name</label>
      <input
        type="text"
        value={value}
        placeholder={placeholder}
        autoComplete="off"
        title={value || undefined}
        onFocus={() => onShowList(true)}
        onBlur={() => setTimeout(() => onShowList(false), 150)}
        onChange={(e) => {
          onChange(e.target.value);
          onShowList(true);
        }}
      />
      {showDropdown && (
        <ul className="messages-approval-filter-autocomplete">
          {loading && <li className="loading">Loading...</li>}
          {!loading &&
            normalizedOptions.map((option, index) => {
              const hoverTitle = [option.name, option.email].filter(Boolean).join(" — ");
              return (
                <li
                  key={`${option.id}-${index}`}
                  title={hoverTitle}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onSelect(option);
                    onShowList(false);
                  }}
                >
                  <div className="messages-approval-filter-requester-option">
                    {option.name && (
                      <span
                        className="messages-approval-filter-requester-option__name"
                        title={option.name}
                      >
                        {option.name}
                      </span>
                    )}
                    {option.email && (
                      <span
                        className="messages-approval-filter-requester-option__email"
                        title={option.email}
                      >
                        {option.email}
                      </span>
                    )}
                  </div>
                </li>
              );
            })}
          {/* {!loading && !hasOptions && value?.trim().length >= MIN_TYPEAHEAD_LENGTH && (
            <li className="loading">No results found</li>
          )} */}
        </ul>
      )}
    </div>
  );
};

const TypeaheadField = ({
  label,
  value,
  placeholder,
  options,
  loading,
  showList,
  onShowList,
  onChange,
  onSelect,
}) => {
  const normalizedOptions = options.map(normalizeOption).filter((opt) => opt.label);
  const hasOptions = normalizedOptions.length > 0;
  const showDropdown = showList && (loading || hasOptions || value?.trim().length >= MIN_TYPEAHEAD_LENGTH);

  return (
    <div className="messages-approval-filter-field messages-approval-filter-field--typeahead">
      <label>{label}</label>
      <input
        type="text"
        value={value}
        placeholder={placeholder}
        autoComplete="off"
        onFocus={() => onShowList(true)}
        onBlur={() => setTimeout(() => onShowList(false), 150)}
        onChange={(e) => {
          onChange(e.target.value, "");
          onShowList(true);
        }}
      />
      {showDropdown && (
        <ul className="messages-approval-filter-autocomplete">
          {loading && <li className="loading">Loading...</li>}
          {!loading &&
            normalizedOptions.map((option, index) => (
              <li
                key={`${option.id}-${index}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  onSelect(option.label, option.id);
                  onShowList(false);
                }}
              >
                {option.label}
              </li>
            ))}
          {/* {!loading && !hasOptions && value?.trim().length >= MIN_TYPEAHEAD_LENGTH && (
            <li className="loading">No results found</li>
          )} */}
        </ul>
      )}
    </div>
  );
};

const MessagesApprovalFilterDrawer = ({
  isOpen,
  onClose,
  draftFilters,
  onDraftChange,
  onApply,
  onReset,
}) => {
  const today = new Date().toISOString().split("T")[0];
  const districtTimer = useRef(null);
  const collegeTimer = useRef(null);
  const requesterTimer = useRef(null);

  const [districtOptions, setDistrictOptions] = useState([]);
  const [collegeOptions, setCollegeOptions] = useState([]);
  const [requesterOptions, setRequesterOptions] = useState([]);
  const [showDistrictList, setShowDistrictList] = useState(false);
  const [showCollegeList, setShowCollegeList] = useState(false);
  const [showRequesterList, setShowRequesterList] = useState(false);
  const [districtLoading, setDistrictLoading] = useState(false);
  const [collegeLoading, setCollegeLoading] = useState(false);
  const [requesterLoading, setRequesterLoading] = useState(false);

  const updateField = (field, value) => {
    onDraftChange({ ...draftFilters, [field]: value });
  };

  const fetchDistricts = useCallback((query) => {
    const trimmed = query?.trim() || "";
    if (trimmed.length < MIN_TYPEAHEAD_LENGTH) {
      setDistrictOptions([]);
      setDistrictLoading(false);
      return;
    }
    setDistrictLoading(true);
    MsgDataService.getApprovalDistrictList(trimmed)
      .then((res) => setDistrictOptions(getListItems(res)))
      .catch(() => setDistrictOptions([]))
      .finally(() => setDistrictLoading(false));
  }, []);

  const fetchColleges = useCallback((query) => {
    const trimmed = query?.trim() || "";
    if (trimmed.length < MIN_TYPEAHEAD_LENGTH) {
      setCollegeOptions([]);
      setCollegeLoading(false);
      return;
    }
    setCollegeLoading(true);
    MsgDataService.getApprovalCollegeList(trimmed)
      .then((res) => setCollegeOptions(getListItems(res)))
      .catch(() => setCollegeOptions([]))
      .finally(() => setCollegeLoading(false));
  }, []);

  const fetchRequesters = useCallback((query) => {
    const trimmed = query?.trim() || "";
    if (trimmed.length < MIN_TYPEAHEAD_LENGTH) {
      setRequesterOptions([]);
      setRequesterLoading(false);
      return;
    }
    setRequesterLoading(true);
    MsgDataService.getApprovalRequesterList(trimmed)
      .then((res) => setRequesterOptions(getListItems(res)))
      .catch(() => setRequesterOptions([]))
      .finally(() => setRequesterLoading(false));
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;
    clearTimeout(districtTimer.current);
    districtTimer.current = setTimeout(() => {
      fetchDistricts(draftFilters.district);
    }, TYPEAHEAD_DEBOUNCE_MS);
    return () => clearTimeout(districtTimer.current);
  }, [draftFilters.district, fetchDistricts, isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    clearTimeout(collegeTimer.current);
    collegeTimer.current = setTimeout(() => {
      fetchColleges(draftFilters.college);
    }, TYPEAHEAD_DEBOUNCE_MS);
    return () => clearTimeout(collegeTimer.current);
  }, [draftFilters.college, fetchColleges, isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    clearTimeout(requesterTimer.current);
    requesterTimer.current = setTimeout(() => {
      fetchRequesters(draftFilters.userName);
    }, TYPEAHEAD_DEBOUNCE_MS);
    return () => clearTimeout(requesterTimer.current);
  }, [draftFilters.userName, fetchRequesters, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setShowDistrictList(false);
      setShowCollegeList(false);
      setShowRequesterList(false);
      setDistrictOptions([]);
      setCollegeOptions([]);
      setRequesterOptions([]);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const canApply = isApprovalDateRangeValid(draftFilters.fromDate, draftFilters.toDate);

  return (
    <>
      <div className="messages-approval-filter-overlay" onClick={onClose} />
      <div className="messages-approval-filter-drawer">
        <div className="messages-approval-filter-drawer__header">
          <h2 className="messages-approval-filter-drawer__title">Filter</h2>
          <button type="button" className="messages-approval-filter-drawer__close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="messages-approval-filter-drawer__body">
          <div className="messages-approval-filter-grid">
            <div className="messages-approval-filter-field">
              <label>Role Wise</label>
              <select
                value={draftFilters.role}
                onChange={(e) => updateField("role", e.target.value)}
              >
                <option value="">All</option>
                <option value="recruiter">Recruiter</option>
                <option value="counselor">Counselor</option>
              </select>
            </div>

            <RequesterTypeaheadField
              value={draftFilters.userName}
              placeholder="Search by name or email"
              options={requesterOptions}
              // loading={requesterLoading}
              showList={showRequesterList}
              onShowList={setShowRequesterList}
              onChange={(text) =>
                onDraftChange({ ...draftFilters, userName: text, userId: "" })
              }
              onSelect={(option) =>
                onDraftChange({
                  ...draftFilters,
                  userName: option.displayName || option.name || option.email,
                  userId: option.id,
                })
              }
            />

            <TypeaheadField
              label="District"
              value={draftFilters.district}
              placeholder="Search district"
              options={districtOptions}
              // loading={districtLoading}
              showList={showDistrictList}
              onShowList={setShowDistrictList}
              onChange={(label, id) =>
                onDraftChange({ ...draftFilters, district: label, districtId: id })
              }
              onSelect={(label, id) =>
                onDraftChange({ ...draftFilters, district: label, districtId: id })
              }
            />

            <TypeaheadField
              label="College"
              value={draftFilters.college}
              placeholder="Search college"
              options={collegeOptions}
              // loading={collegeLoading}
              showList={showCollegeList}
              onShowList={setShowCollegeList}
              onChange={(label, id) =>
                onDraftChange({ ...draftFilters, college: label, collegeId: id })
              }
              onSelect={(label, id) =>
                onDraftChange({ ...draftFilters, college: label, collegeId: id })
              }
            />

            <div className="messages-approval-filter-field">
              <label>Request Status</label>
              <select
                value={draftFilters.adminAction}
                onChange={(e) => updateField("adminAction", e.target.value)}
              >
                <option value="">All</option>
                <option value="approved">Approved</option>
                <option value="decline">Declined</option>
                <option value="pending">Pending</option>
                <option value="sent">Sent</option>
              </select>
            </div>

            <div className="messages-approval-filter-field">
              {/* <label>Status</label>
              <select
                value={draftFilters.status}
                onChange={(e) => updateField("status", e.target.value)}
              >
                <option value="">All</option>
                <option value="sent">Sent</option>
              </select> */}
            </div>

            <div className="messages-approval-filter-field">
              <label>From Date</label>
              <input
                type="date"
                value={draftFilters.fromDate}
                max={draftFilters.toDate || today}
                onChange={(e) => updateField("fromDate", e.target.value)}
              />
            </div>

            <div className="messages-approval-filter-field">
              <label>To Date</label>
              <input
                type="date"
                value={draftFilters.toDate}
                min={draftFilters.fromDate || undefined}
                max={today}
                onChange={(e) => updateField("toDate", e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="messages-approval-filter-drawer__footer">
          <button type="button" className="messages-approval-filter-btn reset" onClick={onReset}>
            Reset
          </button>
          <button
            type="button"
            className="messages-approval-filter-btn apply"
            onClick={onApply}
            disabled={!canApply}
          >
            Apply
          </button>
        </div>
      </div>
    </>
  );
};

export const hasActiveApprovalFilters = (filters) => {
  if (!filters) return false;
  const datesActive = hasCompleteApprovalDateRange(filters.fromDate, filters.toDate);
  const otherActive = Object.entries(filters).some(([key, value]) => {
    if (key === "fromDate" || key === "toDate") return false;
    if (key.endsWith("Id") && !value) return false;
    return value != null && String(value).trim() !== "";
  });
  return datesActive || otherActive;
};

export default MessagesApprovalFilterDrawer;
